import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Row, Col } from 'react-bootstrap';
import { Button } from '@components';

import { URI_INTERNAL } from '@app/utils/pathLocations';
import {
    TRANG_THAI_CONG_VIEC_OPTIONS,
    MUC_DO_UU_TIEN,
    mapMucDoUuTienLabel,
} from '@app/store/constants';

import {
    congViecGetByProjectStepAction,
    congViecCreateAction,
    congViecUpdateAction,
    congViecDeleteAction,
} from '@app/store/reducers/congViec';
import {
    taskAssignmentGetAllAction,
    taskAssignmentCreateAction,
    taskAssignmentUpdateAction,
    taskAssignmentDeleteAction,
} from '@app/store/reducers/taskAssignment';

import { nhanVienGetAllAction } from '@app/store/reducers/nhanVien';
import { projectGetByIdAction } from '@app/store/reducers/project';
import { projectStepGetByProjectIdAction } from '@app/store/reducers/projectStep';

import { toast } from 'react-toastify';
import { messageToast } from '@app/utils/define';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const toStatusKey = (valueOrKey) => {
    const opt = TRANG_THAI_CONG_VIEC_OPTIONS.find(
        (o) => o.value === valueOrKey || o.key === valueOrKey
    );
    return opt ? opt.key : 'canlam';
};

const toStatusValue = (valueOrKey) => {
    const opt = TRANG_THAI_CONG_VIEC_OPTIONS.find(
        (o) => o.value === valueOrKey || o.key === valueOrKey
    );
    return opt ? opt.value : 0;
};

const clampPercent = (n) => {
    const v = typeof n === 'number' && !Number.isNaN(n) ? n : 0;
    return Math.max(0, Math.min(100, v));
};

const getEffectiveTienDo = (task) => {
    const statusKey = toStatusKey(task?.status);
    if (statusKey === 'hoanthanh') return 100;

    return clampPercent(task?.tienDo);
};


const toInputDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getTodayInputDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toDisplayDate = (dateStr) => {
    if (!dateStr) return '';

    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-');
        return `${d}/${m}/${y}`;
    }

    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) return '';

    const d = String(dt.getDate()).padStart(2, '0');
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const y = dt.getFullYear();
    return `${d}/${m}/${y}`;
};


const DEADLINE_SOON_DAYS = 2;
const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const isDateOnly = (s) =>
    typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const parseHanChotAsEndOfDay = (raw) => {
    if (!raw) return null;

    if (isDateOnly(raw)) {
        const [y, m, d] = raw.split('-').map((n) => parseInt(n, 10));
        return new Date(y, m - 1, d, 23, 59, 59, 999);
    }

    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return null;

    if (
        dt.getHours() === 0 &&
        dt.getMinutes() === 0 &&
        dt.getSeconds() === 0
    ) {
        return new Date(
            dt.getFullYear(),
            dt.getMonth(),
            dt.getDate(),
            23,
            59,
            59,
            999
        );
    }

    return dt;
};

const formatDeltaText = (diffMs, isOverdue) => {
    const abs = Math.abs(diffMs);

    if (abs < MS_PER_DAY) {
        const hours = Math.max(1, Math.ceil(abs / MS_PER_HOUR));
        return isOverdue ? `Trễ ${hours} giờ` : `Còn ${hours} giờ`;
    }

    const days = Math.max(1, Math.ceil(abs / MS_PER_DAY));
    return isOverdue ? `Trễ ${days} ngày` : `Còn ${days} ngày`;
};

const getDeadlineWarning = (task) => {
    const statusKey = toStatusKey(task?.status);
    if (statusKey === 'hoanthanh') return null;

    const rawHanChot = task?.hanChot;
    const due = parseHanChotAsEndOfDay(rawHanChot);
    if (!due) return null;

    const now = new Date();
    const diffMs = due.getTime() - now.getTime();

    if (diffMs < 0) {
        const badgeText = `Quá hạn • ${formatDeltaText(diffMs, true)}`;
        return {
            type: 'overdue',
            badgeText,
            tooltip: `Hạn chót: ${toDisplayDate(rawHanChot)} (đã quá hạn)`,
            cardStyle: { borderLeft: '4px solid #dc3545' },
            badgeStyle: {
                marginLeft: 6,
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                background: '#f8d7da',
                color: '#721c24',
                border: '1px solid #f5c6cb',
                whiteSpace: 'nowrap',
            },
        };
    }

    if (diffMs <= DEADLINE_SOON_DAYS * MS_PER_DAY) {
        const badgeText = `Sắp hết hạn • ${formatDeltaText(diffMs, false)}`;
        return {
            type: 'soon',
            badgeText,
            tooltip: `Hạn chót: ${toDisplayDate(
                rawHanChot
            )} (còn ≤ ${DEADLINE_SOON_DAYS} ngày)`,
            cardStyle: { borderLeft: '4px solid #ffc107' },
            badgeStyle: {
                marginLeft: 6,
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                background: '#fff3cd',
                color: '#856404',
                border: '1px solid #ffeeba',
                whiteSpace: 'nowrap',
            },
        };
    }

    return null;
};

const getNhanVienName = (nv) =>
    nv?.name ||
    nv?.hoTen ||
    nv?.fullName ||
    nv?.tenNhanVien ||
    `Nhân viên #${nv?.id}`;

const extractErrorMessage = (err) => {
    let msg =
        (err && err.message) ||
        (typeof err === 'string' ? err : '') ||
        (err && err.title) ||
        '';

    if (!msg && err && err.errors) {
        msg = Object.values(err.errors).flat().join('; ');
    }
    return msg;
};

const COLUMN_STYLE_MAP = {
    canlam: {
        headerBg: '#e1e5f2',
        headerText: '#333333',
        border: '#c4ccdd',
        bodyBg: '#f7f9fc',
    },
    danglam: {
        headerBg: '#d3e5ff',
        headerText: '#245b8f',
        border: '#aac6ff',
        bodyBg: '#f3f7ff',
    },
    choduyet: {
        headerBg: '#ffe0b2',
        headerText: '#8f6a24',
        border: '#ffc875',
        bodyBg: '#fff5e0',
    },
    hoanthanh: {
        headerBg: '#cef4e4',
        headerText: '#247a55',
        border: '#a0e0c6',
        bodyBg: '#f1fbf6',
    },
};

const TaskForm = ({
    mode,
    initialTask,
    initialNhanVienId,
    availableNhanVien,
    onSave,
    onCancel,
    submitting,
}) => {
    const [title, setTitle] = useState('');
    const [moTa, setMoTa] = useState('');
    const [mucDo, setMucDo] = useState(2);
    const [ngayBatDau, setNgayBatDau] = useState('');
    const [hanChot, setHanChot] = useState('');
    const [nhanVienId, setNhanVienId] = useState('');
    const [tienDo, setTienDo] = useState('0');

    useEffect(() => {
        if (initialTask && mode === 'edit') {
            setTitle(initialTask.title || '');
            setMoTa(initialTask.moTa || '');
            setMucDo(
                typeof initialTask.mucDo === 'number' ? initialTask.mucDo : 2
            );

            setNgayBatDau(
                toInputDate(initialTask.ngayBatDau || initialTask.startDate)
            );
            setHanChot(toInputDate(initialTask.hanChot));

            if (typeof initialTask.tienDo === 'number') {
                setTienDo(String(initialTask.tienDo));
            } else {
                setTienDo('0');
            }
        } else if (mode === 'create') {
            setTitle('');
            setMoTa('');
            setMucDo(2);

            setNgayBatDau(getTodayInputDate());

            setHanChot('');
            setTienDo('0');
        }
        setNhanVienId(initialNhanVienId || '');

    }, [initialTask, initialNhanVienId, mode]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!title.trim()) {
            toast.error('Vui lòng nhập tên công việc');
            return;
        }

        let tienDoValue = tienDo === '' ? 0 : Number(tienDo);
        if (Number.isNaN(tienDoValue)) {
            toast.error('Tiến độ không hợp lệ');
            return;
        }
        if (tienDoValue < 0 || tienDoValue > 100) {
            toast.error('Tiến độ phải nằm trong khoảng 0 - 100');
            return;
        }

        onSave({
            title: title.trim(),
            moTa: moTa ? moTa.trim() : null,
            mucDo,
            ngayBatDau: ngayBatDau || null,
            hanChot: hanChot || null,
            nhanVienId: nhanVienId || null,
            tienDo: tienDoValue,
        });
    };

    return (
        <div className="card mb-2 shadow-sm">
            <div className="card-body p-2">
                <h5 className="card-title" style={{ fontSize: 15 }}>
                    {mode === 'edit' ? 'Cập nhật công việc' : 'Thêm công việc mới'}
                </h5>
                <form onSubmit={handleSubmit}>
                    <div className="form-group mb-2">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Tên công việc..."
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>
                    <div className="form-group mb-2">
                        <textarea
                            className="form-control form-control-sm"
                            rows={3}
                            placeholder="Mô tả..."
                            value={moTa || ''}
                            onChange={(e) => setMoTa(e.target.value)}
                        />
                    </div>
                    <div className="form-group mb-2">
                        <select
                            className="form-control form-control-sm"
                            value={nhanVienId || ''}
                            onChange={(e) =>
                                setNhanVienId(
                                    e.target.value
                                        ? parseInt(e.target.value, 10)
                                        : ''
                                )
                            }
                        >
                            <option value="">-- Người phụ trách (tuỳ chọn) --</option>
                            {availableNhanVien.map((nv) => (
                                <option key={nv.id} value={nv.id}>
                                    {getNhanVienName(nv)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group mb-2">
                        <label>Ngày bắt đầu</label>
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={ngayBatDau || ''}
                            onChange={(e) => setNgayBatDau(e.target.value || '')}
                        />
                    </div>

                    <div className="row">
                        <div className="form-group col-12 mb-2">
                            <label className="mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
                            Mức độ ưu tiên
                            </label>
                            <select
                            className="form-control form-control-sm"
                            value={mucDo}
                            onChange={(e) => setMucDo(parseInt(e.target.value, 10))}
                            >
                            {MUC_DO_UU_TIEN.map((m) => (
                                <option key={m.value} value={m.value}>
                                {m.label}
                                </option>
                            ))}
                            </select>
                        </div>

                        <div className="form-group col-12 mb-2">
                            <label className="mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
                            Tiến độ (%)
                            </label>
                            <input
                            type="number"
                            className="form-control form-control-sm"
                            min={0}
                            max={100}
                            value={tienDo}
                            onChange={(e) => setTienDo(e.target.value)}
                            placeholder="0 - 100"
                            />
                        </div>

                        <div className="form-group col-12 mb-2">
                            <label className="mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
                            Hạn chót
                            </label>
                            <input
                            type="date"
                            className="form-control form-control-sm"
                            value={hanChot || ''}
                            onChange={(e) => setHanChot(e.target.value || '')}
                            />
                        </div>
                    </div>

                    <div className="d-flex justify-content-between">
                        <Button
                            type="submit"
                            theme="primary"
                            size="sm"
                            disabled={submitting}
                            isLoading={submitting}
                        >
                            {mode === 'edit' ? 'Lưu' : 'Thêm'}
                        </Button>
                        <Button
                            type="button"
                            theme="secondary"
                            size="sm"
                            onClick={onCancel}
                            disabled={submitting}
                        >
                            Hủy
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const TaskCard = ({ task, assignment, onEdit, onDelete, onDragStart, onDragEnd }) => {
    const startStr = toDisplayDate(task.ngayBatDau || task.startDate);
    const endStr = toDisplayDate(task.hanChot);

    const deadlineWarn = getDeadlineWarning(task);

    return (
        <div
            className="card mb-2 shadow-sm"
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            style={deadlineWarn?.cardStyle}
        >
            <div className="card-body p-2">
                <h6 className="mb-1">{task.title}</h6>

                {task.moTa && (
                    <p className="mb-1 text-muted" style={{ fontSize: 12 }}>
                        {task.moTa}
                    </p>
                )}

                <div
                    className="d-flex justify-content-between align-items-center mb-1"
                    style={{ fontSize: 12 }}
                >
                    <span>Mức độ ưu tiên: {mapMucDoUuTienLabel(task.mucDo)}</span>

                    <div className="text-right">
                        {startStr && <div>Bắt đầu: {startStr}</div>}

                        {endStr && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <div>Hạn chót: {endStr}</div>

                                {deadlineWarn && (
                                    <div style={{ marginTop: 4 }}>
                                        <span
                                            title={deadlineWarn.tooltip}
                                            style={deadlineWarn.badgeStyle}
                                        >
                                            {deadlineWarn.badgeText}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="mb-1" style={{ fontSize: 12, color: '#333' }}>
                    Tiến độ: <strong>{getEffectiveTienDo(task)}%</strong>
                </div>

                {assignment && (
                    <div className="mb-1" style={{ fontSize: 12, color: '#555' }}>
                        Người phụ trách: <strong>{assignment.nhanVienName}</strong>
                    </div>
                )}

                <div className="d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-xs btn-link text-primary mr-2"
                        onClick={onEdit}
                    >
                        Sửa
                    </button>
                    <button
                        type="button"
                        className="btn btn-xs btn-link text-danger"
                        onClick={onDelete}
                    >
                        Xoá
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProjectStepBoard = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const query = useQuery();

    const projectIdParam = query.get('projectId');
    const stepIdParam = query.get('stepId');

    const projectId = projectIdParam ? parseInt(projectIdParam, 10) : NaN;
    const stepId = stepIdParam ? parseInt(stepIdParam, 10) : NaN;

    const { item: project } = useSelector((state) => state.project);
    const { items: stepsRaw } = useSelector((state) => state.projectStep);
    const { items: tasks, loading: taskLoading } = useSelector((state) => state.congViec);
    const { list: allNhanVien } = useSelector((state) => state.nhanVien);
    const { list: taskAssignments } = useSelector((state) => state.taskAssignment);

    const [formMode, setFormMode] = useState(null);
    const [formTask, setFormTask] = useState(null);
    const [formNhanVienId, setFormNhanVienId] = useState(null);
    const [formSubmitting, setFormSubmitting] = useState(false);

    const [draggingTaskId, setDraggingTaskId] = useState(null);

    const [autoOpenTaskId, setAutoOpenTaskId] = useState(() => {
        const t = query.get('taskId');
        return t ? parseInt(t, 10) : null;
    });

    useEffect(() => {

        if (!Number.isNaN(projectId)) {
            dispatch(projectGetByIdAction(projectId));
            dispatch(projectStepGetByProjectIdAction(projectId));
        }
    }, [dispatch, projectId]);

    useEffect(() => {
        if (!Number.isNaN(stepId)) {
            dispatch(congViecGetByProjectStepAction(stepId));
            dispatch(taskAssignmentGetAllAction());
        }
        dispatch(nhanVienGetAllAction());
    }, [dispatch, stepId]);

    const currentStep = useMemo(() => stepsRaw.find((s) => s.id === stepId), [stepsRaw, stepId]);

    const projectTeamIds = useMemo(() => {
        if (!project || !Array.isArray(project.teams)) return [];
        return project.teams.map((t) => t.teamId);
    }, [project]);

    const availableNhanVien = useMemo(() => {
        if (!Array.isArray(allNhanVien) || !projectTeamIds.length) return [];
        return allNhanVien.filter((nv) => projectTeamIds.includes(nv.teamId));
    }, [allNhanVien, projectTeamIds]);

    const assignmentByTaskId = useMemo(() => {
        const map = {};
        (taskAssignments || []).forEach((a) => {
            if (!map[a.congViecId]) {
                map[a.congViecId] = a;
            }
        });
        return map;
    }, [taskAssignments]);

    const tasksByStatus = useMemo(() => {
        const grouped = {
            canlam: [],
            danglam: [],
            choduyet: [],
            hoanthanh: [],
        };

        (tasks || []).forEach((t) => {
            const key = toStatusKey(t.status);
            if (grouped[key]) grouped[key].push(t);
        });

        return grouped;
    }, [tasks]);

    const totalProgress = useMemo(() => {
        if (!tasks || tasks.length === 0) return 0;

        const sum = tasks.reduce((acc, t) => acc + getEffectiveTienDo(t), 0);
        return Math.round(sum / tasks.length);
    }, [tasks]);

    useEffect(() => {
        if (autoOpenTaskId && tasks && tasks.length > 0 && taskAssignments && taskAssignments.length > 0) {
            const task = tasks.find(t => t.id === autoOpenTaskId);
            if (task && !formMode && !formTask) {
                setFormMode('edit');
                setFormTask(task);
                const assign = assignmentByTaskId[task.id];
                setFormNhanVienId(assign ? assign.nhanVienId : null);
                setAutoOpenTaskId(null);

                const currentSearch = new URLSearchParams(history.location.search);
                currentSearch.delete('taskId');
                history.replace({
                    pathname: history.location.pathname,
                    search: currentSearch.toString() ? `?${currentSearch.toString()}` : '',
                    state: history.location.state
                });
            }
        }
    }, [autoOpenTaskId, tasks, taskAssignments, assignmentByTaskId, formMode, formTask]);


    const handleOpenCreate = () => {
        setFormMode('create');
        setFormTask(null);
        setFormNhanVienId(null);
    };

    const handleOpenEdit = (task) => {
        setFormMode('edit');
        setFormTask(task);
        const assign = assignmentByTaskId[task.id];
        setFormNhanVienId(assign ? assign.nhanVienId : null);
    };

    const handleCloseForm = () => {
        setFormMode(null);
        setFormTask(null);
        setFormNhanVienId(null);
    };

    const handleSaveForm = async (values) => {
        try {
            setFormSubmitting(true);

            if (formMode === 'create') {
                const createModel = {
                    projectStepId: stepId,
                    title: values.title,
                    moTa: values.moTa,
                    mucDo: values.mucDo,
                    status: 0,
                    ngayBatDau: values.ngayBatDau,
                    hanChot: values.hanChot,
                    tienDo: values.tienDo,
                };

                const created = await dispatch(congViecCreateAction(createModel)).unwrap();

                if (values.nhanVienId) {
                    await dispatch(
                        taskAssignmentCreateAction({
                            congViecId: created.id,
                            nhanVienId: values.nhanVienId,
                            ngayGiao: new Date().toISOString(),
                        })
                    ).unwrap();
                    await dispatch(taskAssignmentGetAllAction());
                }

                toast.success(
                    messageToast.messageToastCreateSuccess || 'Tạo công việc thành công'
                );
            } else if (formMode === 'edit' && formTask) {
                const updateModel = {
                    projectStepId: formTask.projectStepId,
                    title: values.title,
                    moTa: values.moTa,
                    mucDo: values.mucDo,
                    status: formTask.status,
                    ngayBatDau: values.ngayBatDau,
                    hanChot: values.hanChot,
                    tienDo: values.tienDo,
                };

                await dispatch(congViecUpdateAction({ id: formTask.id, data: updateModel })).unwrap();

                const currentAssign = assignmentByTaskId[formTask.id];

                if (values.nhanVienId) {
                    if (currentAssign) {
                        if (currentAssign.nhanVienId !== values.nhanVienId) {
                            await dispatch(
                                taskAssignmentUpdateAction({
                                    id: currentAssign.id,
                                    data: {
                                        congViecId: formTask.id,
                                        nhanVienId: values.nhanVienId,
                                        ngayGiao:
                                            currentAssign.ngayGiao || new Date().toISOString(),
                                    },
                                })
                            ).unwrap();
                        }
                    } else {
                        await dispatch(
                            taskAssignmentCreateAction({
                                congViecId: formTask.id,
                                nhanVienId: values.nhanVienId,
                                ngayGiao: new Date().toISOString(),
                            })
                        ).unwrap();
                    }
                    await dispatch(taskAssignmentGetAllAction());
                } else if (currentAssign) {
                    await dispatch(taskAssignmentDeleteAction(currentAssign.id)).unwrap();
                    await dispatch(taskAssignmentGetAllAction());
                }

                toast.success(
                    messageToast.messageToastUpdateSuccess || 'Cập nhật công việc thành công'
                );
            }

            if (!Number.isNaN(projectId)) {
                dispatch(projectStepGetByProjectIdAction(projectId));
            }

            handleCloseForm();
        } catch (err) {
            const msg = extractErrorMessage(err);
            const baseMsg =
                formMode === 'edit'
                    ? messageToast.messageToastUpdateError || 'Cập nhật công việc thất bại'
                    : messageToast.messageToastCreateError || 'Tạo công việc thất bại';

            toast.error(msg ? `${baseMsg}: ${msg}` : baseMsg);
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleDeleteTask = async (task) => {
        if (!window.confirm('Bạn có chắc muốn xoá công việc này?')) return;

        try {
            await dispatch(congViecDeleteAction(task.id)).unwrap();

            toast.success(
                messageToast.messageToastDeleteSuccess || 'Xoá công việc thành công'
            );

            if (!Number.isNaN(projectId)) {
                dispatch(projectStepGetByProjectIdAction(projectId));
            }
        } catch (err) {
            const msg = extractErrorMessage(err);
            toast.error(
                msg
                    ? `${messageToast.messageToastTryCatch}: ${msg}`
                    : messageToast.messageToastTryCatch
            );
        }
    };

    const handleDragStart = (taskId) => {
        setDraggingTaskId(taskId);
    };

    const handleDragEnd = () => {
        setDraggingTaskId(null);
    };

    const handleDropOnStatus = async (statusKey) => {
        if (!draggingTaskId) return;
        const task = (tasks || []).find((t) => t.id === draggingTaskId);
        if (!task) return;

        const newStatusValue = toStatusValue(statusKey);
        const oldStatusValue = toStatusValue(task.status);

        if (newStatusValue === oldStatusValue) return;

        try {
            const updateModel = {
                projectStepId: task.projectStepId,
                title: task.title,
                moTa: task.moTa,
                mucDo: task.mucDo,
                status: newStatusValue,
                ngayBatDau: task.ngayBatDau,
                hanChot: task.hanChot,
                tienDo: typeof task.tienDo === 'number' ? task.tienDo : 0,
            };

            await dispatch(
                congViecUpdateAction({
                    id: task.id,
                    data: updateModel,
                })
            ).unwrap();

            if (!Number.isNaN(projectId)) {
                dispatch(projectStepGetByProjectIdAction(projectId));
            }
        } catch (err) {
            const msg = extractErrorMessage(err);
            toast.error(
                msg
                    ? `${messageToast.messageToastTryCatch}: ${msg}`
                    : messageToast.messageToastTryCatch
            );
        } finally {
            setDraggingTaskId(null);
        }
    };

    if (Number.isNaN(projectId) || Number.isNaN(stepId)) {
        return <p>Thiếu projectId hoặc stepId.</p>;
    }

    return (
        <>
            <Button
                theme="secondary"
                onClick={() => {
                    const st = history.location.state;
                    if (st && st.fromNhiemVuCaNhan) {
                        history.push(URI_INTERNAL.NHIEMVU_CANHAN_PATH);
                    } else {
                        history.push(`${URI_INTERNAL.PROJECT_DETAIL_PATH}?id=${projectId}`);
                    }
                }}
                className="mb-3"
            >
                ← Quay lại
            </Button>

            <Card className="mb-3">
                <Card.Header as="h5">{currentStep?.quyTrinhName || 'Bước dự án'}</Card.Header>
                <Card.Body>
                    <p className="text-muted mb-3">
                        {project?.name ? `Bước trong dự án: ${project.name}` : ''}
                    </p>

                    <Row className="align-items-center">
                        <Col md={3}>
                            <strong>Tiến độ bước</strong>
                            <p className="mb-0">{totalProgress}%</p>
                        </Col>
                        <Col md={9}>
                            <div className="progress" style={{ height: 8, marginTop: 5 }}>
                                <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${totalProgress}%` }}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                />
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <h4 className="mb-3">Bảng công việc - {currentStep?.quyTrinhName || ''}</h4>

            {taskLoading && <p className="text-muted">Đang tải danh sách công việc...</p>}

            <Row>
                {['canlam', 'danglam', 'choduyet', 'hoanthanh'].map((statusKey) => {
                    const opt = TRANG_THAI_CONG_VIEC_OPTIONS.find((o) => o.key === statusKey);
                    const label = opt ? opt.label : statusKey;
                    const list = tasksByStatus[statusKey] || [];

                    const deadlineCounts = list.reduce(
                        (acc, t) => {
                            const w = getDeadlineWarning(t);
                            if (!w) return acc;
                            if (w.type === 'overdue') acc.overdue += 1;
                            if (w.type === 'soon') acc.soon += 1;
                            return acc;
                        },
                        { overdue: 0, soon: 0 }
                    );

                    const styleCfg = COLUMN_STYLE_MAP[statusKey] || COLUMN_STYLE_MAP.canlam;

                    return (
                        <Col
                            key={statusKey}
                            md={3}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => handleDropOnStatus(statusKey)}
                        >
                            <div
                                className="card mb-3 shadow-sm"
                                style={{
                                    borderColor: styleCfg.border,
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                    backgroundColor: '#ffffff',
                                }}
                            >
                                <div
                                    className="card-header d-flex align-items-center"
                                    style={{
                                        backgroundColor: styleCfg.headerBg,
                                        borderBottomColor: styleCfg.border,
                                        borderBottomWidth: 1,
                                        borderBottomStyle: 'solid',
                                        color: styleCfg.headerText,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        height: 52,
                                        paddingTop: 8,
                                        paddingBottom: 8,
                                    }}
                                >
                                    <div
                                        className="d-flex align-items-center"
                                        style={{ gap: 6, whiteSpace: 'nowrap' }}
                                    >
                                        <span>{label}</span>

                                        {deadlineCounts.overdue > 0 && (
                                            <span
                                                title="Số công việc quá hạn (chưa hoàn thành)"
                                                style={{
                                                    padding: '2px 6px',
                                                    borderRadius: 999,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    background: '#f8d7da',
                                                    color: '#721c24',
                                                    border: '1px solid #f5c6cb',
                                                }}
                                            >
                                                Quá hạn: {deadlineCounts.overdue}
                                            </span>
                                        )}

                                        {deadlineCounts.soon > 0 && (
                                            <span
                                                title={`Số công việc sắp hết hạn (≤ ${DEADLINE_SOON_DAYS} ngày, chưa hoàn thành)`}
                                                style={{
                                                    padding: '2px 6px',
                                                    borderRadius: 999,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    background: '#fff3cd',
                                                    color: '#856404',
                                                    border: '1px solid #ffeeba',
                                                }}
                                            >
                                                Sắp hết hạn: {deadlineCounts.soon}
                                            </span>
                                        )}
                                    </div>

                                    <div
                                        style={{
                                            flex: 1,
                                            textAlign: 'center',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {list.length}
                                    </div>

                                    {statusKey === 'canlam' && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary d-flex align-items-center justify-content-center p-0"
                                            onClick={handleOpenCreate}
                                            title="Thêm công việc mới"
                                            style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 6,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 18,
                                                    lineHeight: 1,
                                                    position: 'relative',
                                                    top: -1,
                                                }}
                                            >
                                                +
                                            </span>
                                        </button>
                                    )}
                                </div>

                                <div
                                    className="card-body"
                                    style={{
                                        minHeight: 260,
                                        backgroundColor: styleCfg.bodyBg,
                                    }}
                                >
                                    {statusKey === 'canlam' && formMode && (
                                        <TaskForm
                                            mode={formMode}
                                            initialTask={formTask}
                                            initialNhanVienId={formNhanVienId}
                                            availableNhanVien={availableNhanVien}
                                            onSave={handleSaveForm}
                                            onCancel={handleCloseForm}
                                            submitting={formSubmitting}
                                        />
                                    )}

                                    {list.map((task) => (
                                        <TaskCard
                                            key={task.id}
                                            task={task}
                                            assignment={assignmentByTaskId[task.id]}
                                            onEdit={() => handleOpenEdit(task)}
                                            onDelete={() => handleDeleteTask(task)}
                                            onDragStart={() => handleDragStart(task.id)}
                                            onDragEnd={handleDragEnd}
                                        />
                                    ))}
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>
        </>
    );
};

export default ProjectStepBoard;
